import React, { useEffect, useState } from "react";
import SafeIcon from "../../../common/SafeIcon";
import { FiDownload, FiFileText, FiLoader, FiX } from "react-icons/fi";
import { clientDocumentService } from "../../../services/client/clientDocumentService";

function isImage(type) {
  return String(type || "").startsWith("image/");
}

export default function ClientDocumentPreviewModal({
  organizationId,
  document,
  onClose,
  onDownload,
}) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const requestKey = document ? `${organizationId}:${document.id}` : "";

  useEffect(() => {
    if (!document || !organizationId) {
      setUrl("");
      return undefined;
    }
    let current = true;
    setUrl("");
    setError("");
    setLoading(true);
    clientDocumentService
      .getClientDocumentPreviewUrl({ organizationId, document })
      .then((signedUrl) => { if (current) setUrl(signedUrl); })
      .catch((previewError) => { if (current) setError(previewError?.message || "Preview is unavailable."); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [requestKey]);

  useEffect(() => {
    if (!document) return undefined;
    const keydown = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [document, onClose]);

  if (!document) return null;
  const previewable = document.file_type === "application/pdf" || isImage(document.file_type);

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section role="dialog" aria-modal="true" aria-labelledby="document-preview-title" className="flex max-h-[96dvh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:max-w-5xl sm:rounded-3xl">
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-700"><SafeIcon icon={FiFileText} /></span><h2 id="document-preview-title" className="truncate text-sm font-bold text-slate-900">{document.file_name}</h2></div>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" disabled={loading} onClick={() => onDownload(document)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"><SafeIcon icon={FiDownload} />Download</button>
            <button type="button" aria-label="Close preview" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><SafeIcon icon={FiX} /></button>
          </div>
        </header>
        <div className="min-h-[50vh] flex-1 overflow-auto bg-slate-100 p-3 sm:p-6">
          {loading && <div className="flex min-h-[50vh] items-center justify-center gap-2 text-sm text-slate-500"><SafeIcon icon={FiLoader} className="animate-spin" />Preparing secure preview…</div>}
          {error && <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{error}</p>}
          {!loading && !error && !previewable && <div className="flex min-h-[50vh] flex-col items-center justify-center text-center"><SafeIcon icon={FiFileText} className="text-4xl text-violet-600" /><p className="mt-3 font-bold text-slate-800">Preview isn’t supported for this file type</p><p className="mt-1 text-sm text-slate-500">Download the file to view its contents.</p><button type="button" onClick={() => onDownload(document)} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-bold text-white"><SafeIcon icon={FiDownload} />Secure download</button></div>}
          {!loading && !error && previewable && url && (isImage(document.file_type) ? <img src={url} alt={document.file_name} className="mx-auto max-h-[78vh] max-w-full rounded-lg object-contain shadow" /> : <iframe title={`Preview ${document.file_name}`} src={url} className="h-[75vh] w-full rounded-lg bg-white" />)}
        </div>
      </section>
    </div>
  );
}